import { CARAVAN_TRIP } from './trip'
import type { ChallengeCategory } from '../domain/challenge'

export type DailyMediaKind = 'photo' | 'video'
export type ChallengeContentRole = 'daily_media' | 'daily_challenge' | 'secret_mission'
export type ChallengeFieldKind = 'text' | 'textarea' | 'url' | 'select'

export type ChallengeResponseField = Readonly<{
  key: string
  label: string
  kind: ChallengeFieldKind
  required: boolean
  maxLength?: number
  placeholder?: string
  options?: readonly Readonly<{ value: string; label: string }>[]
}>

export type ChallengeVideoRule = Readonly<{
  required: boolean
  minDurationSeconds?: number
  maxDurationSeconds: number
}>

export type ChallengeResponseDefinition = Readonly<{
  fields: readonly ChallengeResponseField[]
  allowPhoto?: boolean
  video?: ChallengeVideoRule
  secretUntil22?: boolean
  submitLabel: string
}>

export type DailyMediaDefinition = Readonly<{
  id: string
  role: 'daily_media'
  kind: DailyMediaKind
  title: string
  detail: string
  category: ChallengeCategory
  response: ChallengeResponseDefinition
}>

export type DailyChallengeDefinition = Readonly<{
  id: string
  role: 'daily_challenge'
  title: string
  detail: string
  category: ChallengeCategory
  safetyNote: string
  response: ChallengeResponseDefinition
  circularOrder?: readonly string[]
  secretRecipientOffset?: number
}>

export type TripDayProgram = Readonly<{
  productDay: string
  media: DailyMediaDefinition
  challenge: DailyChallengeDefinition
}>

export type SecretMissionDefinition = Readonly<{
  id: string
  role: 'secret_mission'
  title: string
  detail: string
  category: ChallengeCategory
  safetyNote: string
}>

const field = (
  key: string,
  label: string,
  kind: ChallengeFieldKind = 'text',
  options: Partial<Omit<ChallengeResponseField, 'key' | 'label' | 'kind'>> = {},
): ChallengeResponseField => Object.freeze({ key, label, kind, required: true, maxLength: 220, ...options })

const completion = (submitLabel = 'Marquer comme réalisé'): ChallengeResponseDefinition => Object.freeze({
  fields: Object.freeze([]),
  submitLabel,
})

const media = (
  id: string,
  kind: DailyMediaKind,
  title: string,
  detail: string,
): DailyMediaDefinition => Object.freeze({
  id,
  role: 'daily_media',
  kind,
  title,
  detail,
  category: kind === 'video' ? 'humor' : 'observation_photo',
  response: kind === 'video'
    ? Object.freeze({ fields: Object.freeze([]), video: Object.freeze({ required: true, maxDurationSeconds: 30 }), submitLabel: 'Conserver ma vidéo' })
    : completion('Ajouter ma photo'),
})

const challenge = (
  id: string,
  title: string,
  detail: string,
  category: ChallengeCategory,
  response: ChallengeResponseDefinition,
  options: Pick<DailyChallengeDefinition, 'safetyNote' | 'circularOrder' | 'secretRecipientOffset'> = { safetyNote: '' },
): DailyChallengeDefinition => Object.freeze({ id, role: 'daily_challenge', title, detail, category, response, ...options })

const participantIds = CARAVAN_TRIP.participants.map((participant) => participant.id)

export const TRIP_DAY_PROGRAMS: readonly TripDayProgram[] = Object.freeze([
  Object.freeze({
    productDay: '2026-09-06',
    media: media('81000000-0000-4000-8000-000000000006', 'photo', 'Le cliché assumé', 'Réalise la photo la plus cliché touriste possible.'),
    challenge: challenge('82000000-0000-4000-8000-000000000006', 'La commande à l’aveugle', 'Choisis pour la personne suivante un truc marocain qu’elle n’a jamais goûté. Elle le découvre au dernier moment.', 'gastronomy', completion(), {
      safetyNote: '',
      circularOrder: Object.freeze(participantIds),
    }),
  }),
  Object.freeze({
    productDay: '2026-09-07',
    media: media('81000000-0000-4000-8000-000000000007', 'photo', 'Depuis la voiture', 'Prends ta meilleure photo depuis la voiture.'),
    challenge: challenge('82000000-0000-4000-8000-000000000007', 'La playlist des six', 'Chacun ajoute un morceau à la playlist du voyage.', 'culture', Object.freeze({
      fields: Object.freeze([
        field('title', 'Titre du morceau'),
        field('artist', 'Artiste'),
        field('link', 'Lien facultatif', 'url', { required: false, maxLength: 500, placeholder: 'https://…' }),
      ]),
      submitLabel: 'Ajouter mon morceau',
    }), { safetyNote: '' }),
  }),
  Object.freeze({
    productDay: '2026-09-08',
    media: media('81000000-0000-4000-8000-000000000008', 'photo', 'Tout petit', 'Fais paraître une personne minuscule face au paysage.'),
    challenge: challenge('82000000-0000-4000-8000-000000000008', 'Hors algorithme', 'Demande une recommandation à une personne rencontrée sur place.', 'social_interaction', Object.freeze({
      fields: Object.freeze([field('recommendation', 'La recommandation', 'textarea', { maxLength: 240, placeholder: 'Note l’idée en quelques mots…' })]),
      submitLabel: 'Conserver la recommandation',
    }), { safetyNote: 'Demander avec respect, accepter tout refus et privilégier une interaction naturelle dans un lieu sûr.' }),
  }),
  Object.freeze({
    productDay: '2026-09-09',
    media: media('81000000-0000-4000-8000-000000000009', 'photo', 'Le décor principal', 'Réalise une image qui pourrait ouvrir un film d’aventure.'),
    challenge: challenge('82000000-0000-4000-8000-000000000009', 'La prédiction foireuse', 'Écris trois prédictions foireuses pour la journée. Révélation ce soir.', 'humor', Object.freeze({
      fields: Object.freeze([
        field('route', 'Prédiction sur le trajet', 'textarea'),
        field('member', 'Prédiction sur un membre du groupe', 'textarea'),
        field('before22', 'Prédiction avant 22 h', 'textarea'),
      ]),
      secretUntil22: true,
      submitLabel: 'Sceller mes prédictions',
    }), { safetyNote: '' }),
  }),
  Object.freeze({
    productDay: '2026-09-10',
    media: media('81000000-0000-4000-8000-000000000010', 'video', 'Remake marocain', 'Rejoue une scène de film, version marocaine et petits moyens.'),
    challenge: challenge('82000000-0000-4000-8000-000000000010', 'L’inconnu décide', 'Laisse une personne extérieure au groupe choisir entre deux options pour toi.', 'social_interaction', Object.freeze({
      fields: Object.freeze([field('proof', 'Ce que la personne a choisi', 'textarea', { maxLength: 240 })]),
      allowPhoto: true,
      submitLabel: 'Conserver cette décision',
    }), { safetyNote: 'Demander avec respect et accord explicite, accepter tout refus et limiter le choix à une décision sans risque dans un lieu sûr.' }),
  }),
  Object.freeze({
    productDay: '2026-09-11',
    media: media('81000000-0000-4000-8000-000000000011', 'photo', 'Golden hour', 'Réalise la plus belle photo de golden hour ou de coucher de soleil.'),
    challenge: challenge('82000000-0000-4000-8000-000000000011', 'Deux mensonges et une vérité', 'Prépare deux mensonges et une vérité à raconter ce soir.', 'humor', Object.freeze({
      fields: Object.freeze([
        field('story1', 'Anecdote 1', 'textarea'),
        field('story2', 'Anecdote 2', 'textarea'),
        field('story3', 'Anecdote 3', 'textarea'),
        field('truthIndex', 'La véritable anecdote', 'select', { options: Object.freeze([{ value: '1', label: 'Anecdote 1' }, { value: '2', label: 'Anecdote 2' }, { value: '3', label: 'Anecdote 3' }]) }),
      ]),
      secretUntil22: true,
      submitLabel: 'Sceller mes trois anecdotes',
    }), { safetyNote: '' }),
  }),
  Object.freeze({
    productDay: '2026-09-12',
    media: media('81000000-0000-4000-8000-000000000012', 'photo', 'Le reflet', 'Fais du reflet le sujet de ta photo.'),
    challenge: challenge('82000000-0000-4000-8000-000000000012', 'Le son du jour', 'Demande à quelqu’un quel morceau il écoute en ce moment.', 'social_interaction', Object.freeze({
      fields: Object.freeze([
        field('title', 'Titre du morceau'),
        field('artist', 'Artiste éventuel', 'text', { required: false }),
        field('link', 'Lien facultatif', 'url', { required: false, maxLength: 500, placeholder: 'https://…' }),
      ]),
      submitLabel: 'Conserver ce morceau',
    }), { safetyNote: 'Demander avec respect, accepter tout refus et rester dans une interaction naturelle et sûre.' }),
  }),
  Object.freeze({
    productDay: '2026-09-13',
    media: media('81000000-0000-4000-8000-000000000013', 'photo', 'Bleu Essaouira', 'Fais du bleu le vrai sujet de ta photo.'),
    challenge: challenge('82000000-0000-4000-8000-000000000013', 'Le menu inconnu', 'Goûte quelque chose de complètement nouveau.', 'gastronomy', completion(), { safetyNote: '' }),
  }),
  Object.freeze({
    productDay: '2026-09-14',
    media: media('81000000-0000-4000-8000-000000000014', 'photo', 'Portrait de pote', 'Fais le meilleur portrait d’un membre du groupe.'),
    challenge: challenge('82000000-0000-4000-8000-000000000014', 'Le commentaire sportif', 'Commente une action banale d’un pote comme une finale mondiale.', 'humor', Object.freeze({
      fields: Object.freeze([]),
      video: Object.freeze({ required: true, minDurationSeconds: 10, maxDurationSeconds: 20 }),
      submitLabel: 'Conserver mon commentaire',
    }), { safetyNote: 'Filmer avec l’accord de la personne concernée.' }),
  }),
  Object.freeze({
    productDay: '2026-09-15',
    media: media('81000000-0000-4000-8000-000000000015', 'video', 'Trend Reel/TikTok', 'Reproduis une trend Reel ou TikTok au choix.'),
    challenge: challenge('82000000-0000-4000-8000-000000000015', 'Le juste prix', 'Obtiens une réduction ou un petit extra sur un achat.', 'social_interaction', completion(), { safetyNote: '' }),
  }),
  Object.freeze({
    productDay: '2026-09-16',
    media: media('81000000-0000-4000-8000-000000000016', 'photo', 'La photo d’affiche', 'Crée une photo digne d’une affiche de film.'),
    challenge: challenge('82000000-0000-4000-8000-000000000016', 'Le meilleur achat à 20 MAD', 'Trouve le meilleur objet possible pour 20 dirhams.', 'mini_adventure', Object.freeze({
      fields: Object.freeze([field('purchase', 'Ton achat', 'textarea', { maxLength: 180 })]),
      secretUntil22: true,
      submitLabel: 'Garder mon achat secret',
    }), { safetyNote: 'Budget maximal : 20 MAD.' }),
  }),
  Object.freeze({
    productDay: '2026-09-17',
    media: media('81000000-0000-4000-8000-000000000017', 'photo', 'Pris dans une mauvaise situation', 'Mets en scène une situation regrettable, mais sans danger.'),
    challenge: challenge('82000000-0000-4000-8000-000000000017', 'Darija IRL', 'Place trois expressions en darija dans de vraies conversations.', 'culture', Object.freeze({
      fields: Object.freeze([field('expressions', 'Les expressions utilisées', 'textarea', { maxLength: 240 })]),
      submitLabel: 'Conserver mes expressions',
    }), { safetyNote: 'Interagir avec respect et accepter de ne pas poursuivre si la personne n’est pas disponible.' }),
  }),
  Object.freeze({
    productDay: '2026-09-18',
    media: media('81000000-0000-4000-8000-000000000018', 'photo', 'La photo officielle du groupe', 'Fais la photo qui représentera tout le voyage.'),
    challenge: challenge('82000000-0000-4000-8000-000000000018', 'Le highlight de l’autre', 'Attribue à la personne reçue en secret le prix de son meilleur moment.', 'culture', Object.freeze({
      fields: Object.freeze([field('award', 'Le prix et son meilleur moment', 'textarea', { maxLength: 260 })]),
      secretUntil22: true,
      submitLabel: 'Sceller mon highlight',
    }), { safetyNote: '', secretRecipientOffset: 1 }),
  }),
])

export const SECRET_MISSIONS: readonly SecretMissionDefinition[] = Object.freeze([
  Object.freeze({ id: '83000000-0000-4000-8000-000000000001', role: 'secret_mission', title: 'Le mot parasite', detail: 'Invente un mot ou une expression. Avant la fin du voyage, au moins trois autres personnes doivent l’utiliser spontanément sans connaître ta mission.', category: 'humor', safetyNote: '' }),
  Object.freeze({ id: '83000000-0000-4000-8000-000000000002', role: 'secret_mission', title: 'Le totem infiltré', detail: 'Choisis un objet que quelqu’un pourrait réellement avoir avec lui : peigne, câble, chargeur, chaussette, cuillère, paquet de mouchoirs ou autre objet ordinaire. Fais-le apparaître dans au moins huit photos réparties sur cinq étapes. L’objet doit être placé dans un contexte où il n’a aucune raison d’être, et non simplement être utilisé normalement.', category: 'observation_photo', safetyNote: '' }),
  Object.freeze({ id: '83000000-0000-4000-8000-000000000003', role: 'secret_mission', title: 'Le photobomber clandestin', detail: 'Apparais discrètement à l’arrière-plan d’au moins six photos prises par les autres, sans être le sujet prévu et sans te faire repérer au moment de la prise.', category: 'humor', safetyNote: 'Ne jamais perturber une situation sensible ni l’image d’une personne extérieure au groupe.' }),
  Object.freeze({ id: '83000000-0000-4000-8000-000000000004', role: 'secret_mission', title: 'La pose récurrente', detail: 'Fais reproduire au groupe exactement la même pose dans quatre étapes différentes, sans annoncer qu’il s’agit d’une série. Le montage complet est révélé le dernier soir.', category: 'observation_photo', safetyNote: '' }),
  Object.freeze({ id: '83000000-0000-4000-8000-000000000005', role: 'secret_mission', title: 'La fausse tradition', detail: 'Invente un rituel plausible mais parfaitement inutile et réussis à le faire reproduire par au moins trois amis pendant trois journées différentes.', category: 'humor', safetyNote: 'Le rituel doit rester sans danger, respectueux et limité au groupe.' }),
  Object.freeze({ id: '83000000-0000-4000-8000-000000000006', role: 'secret_mission', title: 'Le diplomate', detail: 'Dans trois étapes différentes, obtiens naturellement auprès de trois personnes différentes : une expression en darija, une recommandation personnelle et une instruction ou idée amusante que le groupe réalisera. Les interactions doivent rester naturelles et respectueuses, sans insistance.', category: 'social_interaction', safetyNote: 'Respecter tout refus, demander avec tact et rester dans un contexte sûr.' }),
])

export const CANONICAL_CHALLENGE_IDS = new Set([
  ...TRIP_DAY_PROGRAMS.flatMap((program) => [program.media.id, program.challenge.id]),
  ...SECRET_MISSIONS.map((mission) => mission.id),
])

export function getTripDayProgram(productDay: string): TripDayProgram | null {
  return TRIP_DAY_PROGRAMS.find((program) => program.productDay === productDay) ?? null
}

export function getChallengeResponse(challengeId: string): ChallengeResponseDefinition | null {
  for (const program of TRIP_DAY_PROGRAMS) {
    if (program.media.id === challengeId) return program.media.response
    if (program.challenge.id === challengeId) return program.challenge.response
  }
  return null
}

export function getDailyChallengeDefinition(challengeId: string): DailyChallengeDefinition | null {
  return TRIP_DAY_PROGRAMS.find((program) => program.challenge.id === challengeId)?.challenge ?? null
}

export function missionAssignmentRows(): readonly Readonly<{ challengeId: string; memberId: string }>[] {
  return SECRET_MISSIONS.map((mission, index) => Object.freeze({
    challengeId: mission.id,
    memberId: CARAVAN_TRIP.participants[index]?.id ?? '',
  }))
}

export function assignedHighlightRecipient(memberId: string): string | null {
  const index = CARAVAN_TRIP.participants.findIndex((participant) => participant.id === memberId)
  if (index < 0) return null
  return CARAVAN_TRIP.participants[(index + 1) % CARAVAN_TRIP.participants.length]?.id ?? null
}

export function circularRecipient(memberId: string, order: readonly string[]): string | null {
  const index = order.indexOf(memberId)
  if (index < 0 || order.length < 2) return null
  return order[(index + 1) % order.length] ?? null
}
